import config from '../data/status.json';
import { DEMAND_TYPE_FILTER_OPTIONS } from './demandTypeService';

/** @type {Array<{ code: string, label: string, category?: string, active?: boolean, sortOrder?: number, demandTypes?: string[] }>|null} */
let statusRegistry = null;

export const ALL_DEMAND_TYPE_VALUES = DEMAND_TYPE_FILTER_OPTIONS.map((o) => o.value);

export const setStatusRegistry = (definitions) => {
  if (!Array.isArray(definitions)) {
    statusRegistry = null;
    return;
  }
  statusRegistry = definitions
    .filter((d) => d?.code)
    .map((d) => ({
      code: String(d.code).toUpperCase(),
      label: d.label || d.code,
      category: d.category ? String(d.category).toUpperCase() : undefined,
      active: d.active !== false,
      sortOrder: typeof d.sortOrder === 'number' ? d.sortOrder : 0,
      demandTypes: Array.isArray(d.demandTypes)
        ? d.demandTypes.map((t) => String(t).toUpperCase())
        : [...ALL_DEMAND_TYPE_VALUES],
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
};

export const getStatusRegistry = () => statusRegistry;

export const appliesToDemandType = (statusDef, demandType) => {
  if (!demandType) return true;
  const types = statusDef?.demandTypes;
  if (!types || types.length === 0) return true;
  const key = String(demandType).toUpperCase();
  return types.includes(key);
};

const filterByDemandType = (list, demandType) => {
  if (!demandType) return list;
  return list.filter((s) => appliesToDemandType(s, demandType));
};

const fallbackLabel = (code) => {
  const key = String(code).toUpperCase();
  return config.label[key] ?? String(code);
};

export const getStatusLabel = (code) => {
  if (code == null || code === '') return '';
  const key = String(code).toUpperCase();
  const fromApi = statusRegistry?.find((s) => s.code === key);
  return fromApi?.label ?? fallbackLabel(key);
};

export const getStatusDefinition = (code) => {
  if (!code) return null;
  const key = String(code).toUpperCase();
  return statusRegistry?.find((s) => s.code === key) ?? null;
};

export const inferCategoryFromLegacyCode = (code) => {
  const key = String(code).toUpperCase();
  if (key === 'OPEN') return 'INITIAL';
  if (key === 'IN_PROGRESS') return 'IN_PROGRESS';
  if (key === 'PENDING') return 'PENDING';
  if (key === 'RESOLVED') return 'RESOLVED';
  if (key === 'CLOSED') return 'TERMINAL';
  return 'IN_PROGRESS';
};

export const getStatusFilterOptions = (demandType = null) => {
  if (statusRegistry?.length) {
    return filterByDemandType(
      statusRegistry.filter((s) => s.active),
      demandType
    ).map((s) => ({
      value: s.code,
      label: s.label,
      category: s.category,
      demandTypes: s.demandTypes,
    }));
  }
  return Object.keys(config.label).map((key) => ({
    value: config.value[key] ?? key,
    label: config.label[key],
    category: inferCategoryFromLegacyCode(key),
  }));
};

export const getStatusCategory = (code) => {
  const def = getStatusDefinition(code);
  if (def?.category) return def.category;
  return inferCategoryFromLegacyCode(code);
};

export const isTerminalStatus = (code) => getStatusCategory(code) === 'TERMINAL';

/** Categorias que exigem captura de tipo/notas de resolução no FE. */
export const RESOLUTION_CAPTURE_CATEGORIES = ['RESOLVED', 'TERMINAL'];

export const requiresResolutionCapture = (code) =>
  RESOLUTION_CAPTURE_CATEGORIES.includes(getStatusCategory(code));

/** Categorias permitidas como destino ao reabrir ticket fechado (contrato do backend). */
export const REOPEN_TARGET_CATEGORIES = ['INITIAL', 'IN_PROGRESS', 'PENDING'];

export const isReopenTargetStatus = (code) =>
  REOPEN_TARGET_CATEGORIES.includes(getStatusCategory(code));

export const findFirstStatusCodeByCategory = (category, demandType = null) => {
  const cat = String(category).toUpperCase();
  const pool = statusRegistry?.filter((s) => s.active && s.category === cat) ?? [];
  const match = filterByDemandType(pool, demandType)[0];
  return match?.code ?? null;
};

export const getStatusClass = (code) => {
  const category = getStatusCategory(code);
  const map = {
    INITIAL: 'open',
    IN_PROGRESS: 'in_progress',
    PENDING: 'pending',
    RESOLVED: 'resolved',
    TERMINAL: 'closed',
  };
  return map[category] || 'open';
};

export const formatDemandTypesShort = (demandTypes) => {
  if (!demandTypes?.length) return 'Todos os tipos';
  const labels = demandTypes.map((t) => {
    const opt = DEMAND_TYPE_FILTER_OPTIONS.find((o) => o.value === t);
    return opt?.label ?? t;
  });
  return labels.join(', ');
};

export const STATUS_CATEGORY_OPTIONS = [
  { value: 'INITIAL', label: 'Inicial (novo ticket)' },
  { value: 'IN_PROGRESS', label: 'Em andamento' },
  { value: 'PENDING', label: 'Pendente' },
  { value: 'RESOLVED', label: 'Resolvido' },
  { value: 'TERMINAL', label: 'Finalizado (bloqueia edição)' },
];
